import { Outlet, useMatch } from 'react-router';

import { useTenant } from '../../context';

import { RetryPanel } from '@/components/feedback/retry-panel';
import { ReadOnlyBanner } from '@/components/subscription/read-only-banner';
import { Skeleton } from '@/components/ui/skeleton';
import { useSubscription } from '@/hooks/use-subscription';
import { isProvenNoSubscription } from '@/lib/subscription';

/**
 * The subscription step of the router gating chain (ESCLOUD-288). The chain
 * itself — every gate, its mount point and what each does when it cannot get an
 * answer — is declared once in `GATING_CHAIN` (`router/index.tsx`).
 *
 * There is no funnel to resume any more (ESCLOUD-333): funnel v2 creates a
 * tenant only from an activated subscription, so a workspace with no
 * subscription row is legacy data, not a paused purchase. A proven 404 (never
 * a transient error, which gets a retry surface instead: an unproven absence
 * must never redirect) therefore renders a support panel rather than
 * redirecting anywhere - there is no plan screen left to send it to.
 *
 * Every status renders the route. A read-only subscription state - including
 * the legacy never-activated ones - deliberately does NOT redirect: a
 * clinician still reaches existing cases and reports (never destructive on
 * non-payment). It renders a banner and the client disables mutating actions;
 * the server refuses them regardless with `403 subscription_read_only`. The
 * `accessLevel` driving this arrives server-derived on the subscription
 * response - the client never re-derives it from `status`.
 */
export function SubscriptionGuard() {
    const { tenantId } = useTenant();
    const { data: subscription, isLoading, error, refetch } = useSubscription(tenantId);
    // The fullscreen viewer surfaces the read-only state through its own save
    // status and toasts; a layout banner above a full-height canvas would only
    // push it off-screen.
    const onViewerRoute = useMatch('/tenants/:tenantId/cases/:caseId/viewer') !== null;

    if (isLoading) {
        return <SubscriptionGuardSkeleton />;
    }

    if (error || !subscription) {
        if (isProvenNoSubscription(error)) {
            return (
                <RetryPanel
                    title="This workspace has no subscription"
                    description="Workspaces are created together with their subscription, so this one predates the current funnel. Contact contact@therenva.com to restore it."
                    onRetry={() => void refetch()}
                />
            );
        }
        return (
            <RetryPanel
                title="Subscription status unavailable"
                description="Your workspace's subscription could not be checked right now. This is usually temporary."
                onRetry={() => void refetch()}
            />
        );
    }

    if (subscription.accessLevel === 'read_only') {
        return (
            <>
                {!onViewerRoute && tenantId && (
                    <ReadOnlyBanner tenantId={tenantId} subscription={subscription} />
                )}
                <Outlet />
            </>
        );
    }

    return <Outlet />;
}

/**
 * Not the shared route-decision skeleton: this guard sits directly above the
 * dashboard, so the placeholder is the dashboard's own title-plus-tiles shape.
 */
function SubscriptionGuardSkeleton() {
    return (
        <div className="space-y-6 p-6">
            <div>
                <Skeleton className="h-8 w-48" />
                <Skeleton className="mt-2 h-4 w-64" />
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-32 w-full" />
                ))}
            </div>
        </div>
    );
}
